import { parsePartialDateString } from 'src/util/partialDate';

// TGCLOUD-1663 — ordering the VALUES within one label type.
//
// A person can carry several entries of the same kind: six test results, three
// diagnoses, a list of phenotypes. Those rows used to render in whatever order
// the arrays arrived in, which for a Sapio-launched pedigree meant a different
// order on every open (a fresh family is imported per launch). The rows now
// render in an order the preset chooses, per label type.
//
// d3-FREE, pure, and deliberately in its own module: the renderer, the row
// reservation in computeLabelLayout and auto-spacing's measurement pass must all
// see the SAME sequence, so they share one comparator rather than each sorting
// for itself. A sort applied in only one of the three is the drift that paints
// labels at y=0 (TGCLOUD-1610).
//
// Three keys are offered, because no single one works for all the data:
//   'entry' — the order the records were entered (what the chart has always
//             shown, now made deterministic server-side). The honest default.
//   'date'  — clinically the most useful, but only where a date exists. Sapio
//             test payloads carry none at all.
//   'alpha' — the only key that is stable ACROSS opens for Sapio data, since
//             content is all that survives a re-import.

export const SORT_BY = { ENTRY: 'entry', DATE: 'date', ALPHA: 'alpha' };
export const SORT_DIR = { ASC: 'asc', DESC: 'desc' };

const VALID_BY = new Set(Object.values(SORT_BY));
const VALID_DIR = new Set(Object.values(SORT_DIR));

// Per-unit defaults. All 'entry' for now: with the server-side determinism fix
// in place that is exactly what the chart already draws, so switching the
// feature on changes nothing until someone chooses otherwise. The clinically
// correct default per type is a clinical decision, not a developer one.
export const DEFAULT_VALUE_SORT = {
  tests: { by: SORT_BY.ENTRY, dir: SORT_DIR.ASC },
  diseaseWithAge: { by: SORT_BY.ENTRY, dir: SORT_DIR.ASC },
  phenotypes: { by: SORT_BY.ENTRY, dir: SORT_DIR.ASC },
  genotypes: { by: SORT_BY.ENTRY, dir: SORT_DIR.ASC },
  externalPatientId: { by: SORT_BY.ENTRY, dir: SORT_DIR.ASC }
};

// Which sort keys make sense for a unit. External IDs have no date of any kind,
// so offering 'date' there would be a control that silently does nothing.
export const SORT_KEYS_FOR_UNIT = {
  tests: [SORT_BY.ENTRY, SORT_BY.DATE, SORT_BY.ALPHA],
  diseaseWithAge: [SORT_BY.ENTRY, SORT_BY.DATE, SORT_BY.ALPHA],
  phenotypes: [SORT_BY.ENTRY, SORT_BY.DATE, SORT_BY.ALPHA],
  genotypes: [SORT_BY.ENTRY, SORT_BY.DATE, SORT_BY.ALPHA],
  externalPatientId: [SORT_BY.ENTRY, SORT_BY.ALPHA]
};

// Read a unit's sort config out of a labelsSet, falling back to the default and
// rejecting anything unrecognised. A preset is a stored document that outlives
// the code that wrote it, so an unknown key must degrade rather than throw.
export const valueSortFor = (labelsSet, unit) => {
  const fallback = DEFAULT_VALUE_SORT[unit] || {
    by: SORT_BY.ENTRY,
    dir: SORT_DIR.ASC
  };
  const stored = labelsSet?.valueSort?.[unit];
  if (!stored || typeof stored !== 'object') return fallback;
  const allowed = SORT_KEYS_FOR_UNIT[unit] || [SORT_BY.ENTRY];
  const by =
    VALID_BY.has(stored.by) && allowed.includes(stored.by)
      ? stored.by
      : fallback.by;
  const dir = VALID_DIR.has(stored.dir) ? stored.dir : fallback.dir;
  return { by, dir };
};

// Normalise a whole stored sort map: drop units that do not take a sort, and
// replace any unrecognised entry with that unit's default. Used when SAVING a
// preset as well as when reading one, so a corrupt map cannot be written.
export const sanitiseValueSort = (stored) => {
  const out = {};
  Object.keys(SORT_KEYS_FOR_UNIT).forEach((unit) => {
    out[unit] = valueSortFor({ valueSort: stored }, unit);
  });
  return out;
};

// A comparable number for a partial or absent date. Returns null for "no usable
// date" so the caller can bucket those separately rather than guessing a value.
//
// Partial dates are real here: a diagnosis can be recorded as a year alone, and
// genotype `dateRecorded` is an unvalidated string column that may hold anything.
// A missing month or day is treated as the first — the same choice
// siblingBirthKey already makes for a year-only date of birth, so the two
// orderings agree.
export const dateSortValue = (value) => {
  const parts = parsePartialDateString(value);
  if (!parts || !Number.isFinite(parts.year)) return null;
  const month = Number.isFinite(parts.month) ? parts.month : 1;
  const day = Number.isFinite(parts.day) ? parts.day : 1;
  // Plain lexicographic-safe numeric key; never a Date, so no timezone is
  // involved and the result cannot shift with the viewer's offset.
  return parts.year * 10000 + month * 100 + day;
};

// The date a record is sorted by, per label type. Mirrors getRecordDate in the
// clinical-records sidebar so an on-chart date sort and the sidebar's own date
// sort put the same records in the same order.
export const recordDateValue = (unit, record) => {
  if (!record) return null;
  switch (unit) {
    case 'tests':
      // Supplied by the pedigree route as `date` (dateRequested, else
      // dateResultReceived). Null for every Sapio-imported test.
      return dateSortValue(record.date);
    case 'diseaseWithAge':
      // A diagnosis often has an age of diagnosis but no reported date — the
      // inline-capture path stores age alone. Fall back to it so age-only
      // diagnoses order among themselves instead of all landing undated.
      return (
        dateSortValue(record.dateReported) ??
        (Number.isFinite(Number(record.age)) && String(record.age).trim() !== ''
          ? Number(record.age)
          : null)
      );
    case 'phenotypes':
    case 'genotypes':
      return dateSortValue(record.dateRecorded);
    default:
      return null;
  }
};

// The text a record sorts by alphabetically — the same text the label prints,
// so an A-Z sort looks A-Z on the chart.
export const recordSortText = (unit, record) => {
  if (!record) return '';
  switch (unit) {
    case 'tests':
      return String(record.name || record.result || '');
    case 'diseaseWithAge':
      return String(record.alias || record.name || '');
    case 'phenotypes':
      return String(record.name || record.id || '');
    case 'genotypes':
      return String(record.symbol || record.name || record.id || '');
    case 'externalPatientId':
      return String(record || '');
    default:
      return String(record.name || '');
  }
};

// Locale-aware, numeric-aware text compare: "Test 2" before "Test 10".
const compareText = (a, b) =>
  String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base'
  });

// Entry order is the array position the records arrived in. Kept explicit so a
// stable sort is not merely assumed: Array.prototype.sort is specified as stable
// in modern engines, but the comparator returning a real tie-break makes the
// intent legible and survives any future change of container.
const withIndex = (items) => items.map((item, index) => ({ item, index }));

/**
 * Order the values of one label type for rendering.
 *
 * Contract, and the reason each part exists:
 *  - returns a NEW array; callers pass shared member arrays that must not be
 *    mutated (they are also read by the sidebar and by the legend);
 *  - a record with no usable sort key sorts LAST in BOTH directions, matching
 *    RecordBrowserModal. Flipping to descending should not promote the records
 *    we know least about to the top of a clinician's chart;
 *  - when the chosen key is unavailable for EVERY record — the whole-family
 *    Sapio case, where no test carries a date — the result is entry order
 *    rather than an arbitrary shuffle, so the sort degrades to today's
 *    behaviour instead of looking broken;
 *  - every comparison ends in a total order (text, then entry index), so the
 *    same data always produces the same sequence. That determinism is the
 *    correctness property this ticket is really about; the configurability is
 *    the feature on top of it.
 */
export const sortLabelValues = (unit, items, labelsSet) => {
  const list = Array.isArray(items) ? items : [];
  if (list.length < 2) return [...list];

  const { by, dir } = valueSortFor(labelsSet, unit);
  const sign = dir === SORT_DIR.DESC ? -1 : 1;

  if (by === SORT_BY.ENTRY) {
    // Nothing to do: the array already IS entry order. Returning a copy keeps
    // the caller's contract identical whichever branch runs.
    return sign === 1 ? [...list] : [...list].reverse();
  }

  const decorated = withIndex(list);

  if (by === SORT_BY.ALPHA) {
    decorated.sort((a, b) => {
      const cmp = compareText(
        recordSortText(unit, a.item),
        recordSortText(unit, b.item)
      );
      return cmp !== 0 ? sign * cmp : a.index - b.index;
    });
    return decorated.map((x) => x.item);
  }

  // by === DATE
  const keyed = decorated.map((x) => ({
    ...x,
    key: recordDateValue(unit, x.item)
  }));
  const anyDated = keyed.some((x) => x.key !== null);
  if (!anyDated) return [...list]; // degrade to entry order, not to noise

  keyed.sort((a, b) => {
    // Undated last regardless of direction.
    if (a.key === null && b.key === null) return a.index - b.index;
    if (a.key === null) return 1;
    if (b.key === null) return -1;
    if (a.key !== b.key) return sign * (a.key - b.key);
    // Same date: fall to text then entry order so ties are still deterministic.
    const cmp = compareText(
      recordSortText(unit, a.item),
      recordSortText(unit, b.item)
    );
    return cmp !== 0 ? cmp : a.index - b.index;
  });
  return keyed.map((x) => x.item);
};
